export interface ProgressIntervals {
  mainInterval: NodeJS.Timeout | null;
  continuousInterval: NodeJS.Timeout | null;
  transitionInterval: NodeJS.Timeout | null;
  microProgressInterval: NodeJS.Timeout | null;
}

const MIN_INCREMENT = 0.25;
const MICRO_INCREMENT = 0.05;
const NANO_INCREMENT = 0.015;
const ULTRA_NANO_INCREMENT = 0.003;
const TRANSITION_INCREMENT = 0.008;
const MICRO_PROGRESS_INCREMENT = 0.001;
const UPDATE_INTERVAL = 5;
const MICRO_UPDATE_INTERVAL = 50;
const STUCK_THRESHOLD = 50;

interface ProgressState {
  lastProgress: number;
  isGenerating: boolean;
  lastUpdateTime: number;
  stuckCounter: number;
  microProgressCounter: number;
  estimatedTotalSize: number;
  lastCurrentSize: number;
}

// Minimum size increment in bytes to ensure visible progress
// const MIN_SIZE_INCREMENT = 128;
const MIN_SIZE_INCREMENT = 200;

export const createProgressState = (
  estimatedTotalSize: number
): ProgressState => ({
  lastProgress: 0,
  isGenerating: true,
  lastUpdateTime: Date.now(),
  stuckCounter: 0,
  microProgressCounter: 0,
  estimatedTotalSize,
  lastCurrentSize: 0,
});

/**
 * Calculates current size ensuring it always increases visibly when progress increases
 */
const calculateCurrentSize = (
  state: ProgressState,
  progress: number
): number => {
  const proportionalSize = Math.floor(
    (state.estimatedTotalSize * progress) / 100
  );

  // Ensure current size always increases when progress increases
  if (progress > state.lastProgress && state.lastCurrentSize > 0) {
    const minNextSize = state.lastCurrentSize + MIN_SIZE_INCREMENT;
    return Math.max(proportionalSize, minNextSize);
  }

  return proportionalSize;
};

export const ensureMicroProgress = (
  state: ProgressState,
  maxProgress: number,
  onProgress?: (
    progress: number,
    totalSize: number,
    currentSize: number
  ) => void
): NodeJS.Timeout => {
  const interval = setInterval(() => {
    if (!state.isGenerating) {
      clearInterval(interval);
      return;
    }

    const nextProgress = Math.min(
      maxProgress,
      state.lastProgress + MICRO_PROGRESS_INCREMENT
    );
    if (nextProgress > state.lastProgress) {
      const currentSize = calculateCurrentSize(state, nextProgress);
      state.lastProgress = nextProgress;
      state.lastCurrentSize = currentSize;
      onProgress?.(nextProgress, state.estimatedTotalSize, currentSize);
    }
  }, MICRO_UPDATE_INTERVAL);
  return interval;
};

export const isProgressStuck = (state: ProgressState): boolean => {
  const now = Date.now();
  if (now - state.lastUpdateTime > STUCK_THRESHOLD) {
    state.stuckCounter++;
    return true;
  }
  state.stuckCounter = 0;
  state.lastUpdateTime = now;
  return false;
};

export const ensureContinuousProgress = (
  state: ProgressState,
  _currentValue: number,
  maxValue: number,
  onProgress?: (
    progress: number,
    totalSize: number,
    currentSize: number
  ) => void
): NodeJS.Timeout => {
  return setInterval(() => {
    if (!state.isGenerating) return;

    state.microProgressCounter++;

    let increment = ULTRA_NANO_INCREMENT;

    if (isProgressStuck(state)) {
      if (state.stuckCounter > 10) {
        increment = MICRO_INCREMENT;
      } else if (state.stuckCounter > 5) {
        increment = NANO_INCREMENT;
      } else {
        increment = ULTRA_NANO_INCREMENT * (state.stuckCounter + 1);
      }
    }

    if (state.microProgressCounter % 3 === 0) {
      increment += ULTRA_NANO_INCREMENT;
    }

    const nextProgress = Math.min(maxValue, state.lastProgress + increment);
    if (nextProgress > state.lastProgress && nextProgress <= maxValue) {
      const currentSize = calculateCurrentSize(state, nextProgress);
      state.lastProgress = nextProgress;
      state.lastCurrentSize = currentSize;
      onProgress?.(nextProgress, state.estimatedTotalSize, currentSize);
    }
  }, UPDATE_INTERVAL);
};

export const updateProgress = (
  state: ProgressState,
  targetProgress: number,
  onProgress?: (
    progress: number,
    totalSize: number,
    currentSize: number
  ) => void
): void => {
  if (!state.isGenerating) return;
  const newProgress = Math.min(
    targetProgress,
    state.lastProgress + MIN_INCREMENT
  );
  if (newProgress > state.lastProgress) {
    const currentSize = calculateCurrentSize(state, newProgress);
    state.lastProgress = newProgress;
    state.lastCurrentSize = currentSize;
    state.lastUpdateTime = Date.now();
    onProgress?.(newProgress, state.estimatedTotalSize, currentSize);
  }
};

export const startContinuousProgress = (
  state: ProgressState,
  startValue: number,
  endValue: number,
  duration: number,
  useEasing = true,
  onProgress?: (
    progress: number,
    totalSize: number,
    currentSize: number
  ) => void
): ProgressIntervals => {
  const startTime = Date.now();
  const continuousInterval = ensureContinuousProgress(
    state,
    startValue,
    endValue,
    onProgress
  );

  const mainInterval = setInterval(() => {
    if (!state.isGenerating) return;
    const elapsed = Date.now() - startTime;
    let progress = elapsed / duration;

    if (useEasing) {
      progress =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;
    }

    const targetProgress =
      startValue + (endValue - startValue) * Math.min(1, progress);
    updateProgress(state, targetProgress, onProgress);

    if (progress >= 1) {
      clearInterval(mainInterval);
      clearInterval(continuousInterval);
    }
  }, UPDATE_INTERVAL);

  return {
    mainInterval,
    continuousInterval,
    transitionInterval: null,
    microProgressInterval: null,
  };
};

export const startTransitionProgress = (
  state: ProgressState,
  startValue: number,
  endValue: number,
  duration: number,
  onProgress?: (
    progress: number,
    totalSize: number,
    currentSize: number
  ) => void
): NodeJS.Timeout => {
  const startTime = Date.now();

  const transitionInterval = setInterval(() => {
    if (!state.isGenerating) {
      clearInterval(transitionInterval);
      return;
    }

    const elapsed = Date.now() - startTime;
    const progress = Math.min(1, elapsed / duration);

    const easedProgress =
      progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

    const currentProgress =
      startValue + (endValue - startValue) * easedProgress;

    const minProgress = state.lastProgress + TRANSITION_INCREMENT;
    const targetProgress = Math.max(currentProgress, minProgress);

    if (targetProgress > state.lastProgress && targetProgress <= endValue) {
      const currentSize = calculateCurrentSize(state, targetProgress);
      state.lastProgress = targetProgress;
      state.lastCurrentSize = currentSize;
      onProgress?.(targetProgress, state.estimatedTotalSize, currentSize);
    }

    if (progress >= 1) {
      clearInterval(transitionInterval);
    }
  }, UPDATE_INTERVAL);

  return transitionInterval;
};

export const stopProgress = (intervals: ProgressIntervals): void => {
  Object.values(intervals).forEach((interval) => {
    if (interval) clearInterval(interval);
  });
};

/**
 * Helper function to update progress and get the current size with guaranteed increment
 * Use this for direct progress updates outside of interval-based progress
 */
export const updateProgressSize = (
  state: ProgressState,
  progress: number,
  onProgress?: (
    progress: number,
    totalSize: number,
    currentSize: number
  ) => void
): void => {
  const currentSize = calculateCurrentSize(state, progress);
  state.lastProgress = progress;
  state.lastCurrentSize = currentSize;
  onProgress?.(progress, state.estimatedTotalSize, currentSize);
};
